import React from 'react'
import { useForm } from '@inertiajs/react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

interface CreateTeamSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  teams: { id: number; name: string }[]
  parentId?: number
}

export function CreateTeamSheet({ open, onOpenChange, teams, parentId }: CreateTeamSheetProps) {
  const { data, setData, post, processing, errors, reset } = useForm({
    team: {
      name: '',
      parent_id: parentId?.toString() ?? '',
    },
  })

  // Sync parent_id when prop changes (e.g., clicking "+ Child" on different teams)
  React.useEffect(() => {
    setData('team', { ...data.team, parent_id: parentId?.toString() ?? '' })
  }, [parentId])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post('/admin/teams', {
      onSuccess: () => {
        reset()
        onOpenChange(false)
      },
    })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[540px] sm:max-w-[540px]">
        <SheetHeader>
          <SheetTitle>Create Team</SheetTitle>
          <SheetDescription>
            Add a new team to your organization hierarchy.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          <div className="space-y-2">
            <Label htmlFor="team_name">Name</Label>
            <Input
              id="team_name"
              value={data.team.name}
              onChange={(e) => setData('team', { ...data.team, name: e.target.value })}
              required
              placeholder="Enter team name"
              autoFocus
            />
            {errors['team.name'] && (
              <p className="text-sm text-destructive">{errors['team.name']}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="team_parent_id">Parent Team</Label>
            <Select
              value={data.team.parent_id}
              onValueChange={(val) => setData('team', { ...data.team, parent_id: val })}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="None (root team)" />
              </SelectTrigger>
              <SelectContent>
                {teams.map((t) => (
                  <SelectItem key={t.id} value={t.id.toString()}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button type="submit" disabled={processing} className="w-full">
            {processing ? 'Creating...' : 'Create Team'}
          </Button>
        </form>
      </SheetContent>
    </Sheet>
  )
}
